import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import { Label } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Separator } from '@ouropix/ui';
import { toast } from 'sonner';
import {
  Globe,
  RefreshCw,
  Pencil,
  ChevronRight,
  CheckCircle2,
  Clock,
  Settings2,
  Save,
  Folder,
  Image as ImageIcon,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { MediaLibraryModal } from '@/components/media/MediaLibraryModal';

export const Route = createFileRoute('/_admin/seo/')({
  component: SeoIndexPage,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SeoRoute {
  page_slug: string;
  page_type: string;
  has_config: boolean;
  status: 'configured' | 'pending' | 'default' | string;
  last_configured_at: string | null;
  category: string | null;
  published_at: string | null;
  title: string | null;
}

interface SeoDefaults {
  site_name: string;
  og_image: string;
  twitter_card: string;
  robots: string;
  og_type: string;
  language: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CATEGORIES = ['Home', 'Jogos', 'Promoções', 'Compliance', 'Cadastro'];

function StatusBadge({ status, publishedAt }: { status: string; publishedAt: string | null }) {
  if (publishedAt) {
    return (
      <Badge className="gap-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100 text-xs">
        <CheckCircle2 className="h-3 w-3" />
        Publicada
      </Badge>
    );
  }
  if (status === 'configured') {
    return (
      <Badge variant="secondary" className="gap-1 text-xs">
        <Globe className="h-3 w-3" />
        Configurada
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 text-amber-600 border-amber-400 text-xs">
      <Clock className="h-3 w-3" />
      Pendente
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// GlobalDefaults
// ---------------------------------------------------------------------------

function GlobalDefaults() {
  const queryClient = useQueryClient();

  const { data: defaults, isLoading } = useQuery<SeoDefaults>({
    queryKey: ['admin-seo-defaults'],
    queryFn: async () => {
      const res = await api.get('/admin/seo/defaults');
      return res.data;
    },
  });

  const [form, setForm] = useState<SeoDefaults>({
    site_name: 'OuroPix',
    og_image: '',
    twitter_card: 'summary_large_image',
    robots: 'index,follow',
    og_type: 'website',
    language: 'pt-BR',
  });

  const [synced, setSynced] = useState(false);
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  if (!synced && defaults && !isLoading) {
    setForm({ ...defaults });
    setSynced(true);
  }

  const mutation = useMutation({
    mutationFn: async (payload: SeoDefaults) => {
      const res = await api.put('/admin/seo/defaults', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-seo-defaults'] });
      toast.success('Configurações globais de SEO salvas!');
      setSynced(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Erro ao salvar configurações globais');
    },
  });

  if (isLoading) {
    return (
      <div className="rounded-lg border p-6 text-center text-muted-foreground text-sm">
        Carregando configurações globais...
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-6 space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Settings2 className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Configurações Globais</h2>
        <span className="text-xs text-muted-foreground ml-1">
          (defaults aplicados em todas as páginas sem override específico)
        </span>
      </div>
      <Separator />
      <form
        onSubmit={(e) => { e.preventDefault(); mutation.mutate(form); }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="global-site-name">Nome do Site</Label>
            <Input
              id="global-site-name"
              value={form.site_name}
              onChange={(e) => setForm((p) => ({ ...p, site_name: e.target.value }))}
              placeholder="OuroPix"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="global-language">Idioma (language)</Label>
            <Input
              id="global-language"
              value={form.language}
              onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))}
              placeholder="pt-BR"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="global-og-image">OG Image padrão</Label>
            <div className="flex gap-2">
              <Input
                id="global-og-image"
                value={form.og_image}
                onChange={(e) => setForm((p) => ({ ...p, og_image: e.target.value }))}
                placeholder="Cole uma URL ou selecione da biblioteca de mídia"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setMediaLibraryOpen(true)}
                className="gap-2"
              >
                <ImageIcon className="h-4 w-4" />
                {form.og_image ? 'Trocar' : 'Selecionar'}
              </Button>
              {form.og_image && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setForm((p) => ({ ...p, og_image: '' }))}
                  title="Remover imagem"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {form.og_image && (
              <div className="relative rounded overflow-hidden border aspect-[1200/630] max-w-md bg-muted">
                <img
                  loading="lazy"
                  src={form.og_image}
                  alt="OG Image preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Recomendado: 1200×630px (formato Open Graph). Aplicado em todas as páginas sem override.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="global-og-type">OG Type padrão</Label>
            <Input
              id="global-og-type"
              value={form.og_type}
              onChange={(e) => setForm((p) => ({ ...p, og_type: e.target.value }))}
              placeholder="website"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="global-twitter-card">Twitter Card</Label>
            <select
              id="global-twitter-card"
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              value={form.twitter_card}
              onChange={(e) => setForm((p) => ({ ...p, twitter_card: e.target.value }))}
            >
              <option value="summary_large_image">summary_large_image</option>
              <option value="summary">summary</option>
              <option value="app">app</option>
              <option value="player">player</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="global-robots">Robots padrão</Label>
            <select
              id="global-robots"
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              value={form.robots}
              onChange={(e) => setForm((p) => ({ ...p, robots: e.target.value }))}
            >
              <option value="index,follow">index,follow</option>
              <option value="noindex,nofollow">noindex,nofollow</option>
              <option value="noindex,follow">noindex,follow</option>
              <option value="index,nofollow">index,nofollow</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={mutation.isPending} className="gap-2">
            <Save className="h-4 w-4" />
            {mutation.isPending ? 'Salvando...' : 'Salvar Globais'}
          </Button>
        </div>
      </form>

      <MediaLibraryModal
        open={mediaLibraryOpen}
        onOpenChange={setMediaLibraryOpen}
        onSelect={(url) => setForm((p) => ({ ...p, og_image: url }))}
        selectedUrl={form.og_image}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CategoryAccordion
// ---------------------------------------------------------------------------

function CategoryAccordion({
  categoryName,
  routes,
  defaultOpen,
}: {
  categoryName: string;
  routes: SeoRoute[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  const publishedCount  = routes.filter((r) => r.published_at).length;
  const configuredCount = routes.filter((r) => r.status === 'configured' && !r.published_at).length;

  return (
    <div>
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-2">
          <Folder className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">{categoryName}</span>
          <span className="text-xs text-muted-foreground">
            {routes.length} página{routes.length !== 1 ? 's' : ''}
            {publishedCount > 0 && (
              <span className="text-emerald-600 ml-1">· {publishedCount} publicada{publishedCount !== 1 ? 's' : ''}</span>
            )}
            {configuredCount > 0 && (
              <span className="text-sky-600 ml-1">· {configuredCount} configurada{configuredCount !== 1 ? 's' : ''}</span>
            )}
          </span>
        </div>
        <ChevronRight
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-200',
            open && 'rotate-90',
          )}
        />
      </button>
      {open && (
        <div className="border-t divide-y">
          {routes.map((route) => (
            <div
              key={route.page_slug}
              className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 transition-colors gap-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-xs font-mono font-medium truncate max-w-[260px]">
                    {route.page_slug}
                  </code>
                  <StatusBadge status={route.status} publishedAt={route.published_at} />
                </div>
                {route.title && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{route.title}</p>
                )}
              </div>
              <Link
                to="/seo/$slug"
                params={{ slug: (route.page_slug === '/' || !route.page_slug) ? '_root' : route.page_slug.replace(/^\//, '').replace(/\//g, '__') }}
                className="shrink-0"
              >
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Editar SEO desta página">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function SeoIndexPage() {
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching } = useQuery<{ routes: SeoRoute[]; total: number }>({
    queryKey: ['admin-seo-list-all'],
    queryFn: async () => {
      const res = await api.get('/admin/seo?per_page=500');
      const d: any = res.data;
      const rawRoutes: any[] = Array.isArray(d?.routes) ? d.routes : Array.isArray(d) ? d : [];
      return {
        routes: rawRoutes.map((r: any) => ({
          page_slug:          r.page_slug          ?? r.pageSlug          ?? '',
          page_type:          r.page_type          ?? r.pageType          ?? '',
          has_config:         r.has_config         ?? r.hasConfig         ?? false,
          status:             r.status             ?? 'pending',
          last_configured_at: r.last_configured_at ?? r.lastConfiguredAt ?? null,
          category:           r.category           ?? null,
          published_at:       r.published_at       ?? r.publishedAt      ?? null,
          title:              r.title              ?? null,
        })),
        total: d?.total ?? rawRoutes.length,
      };
    },
    placeholderData: (prev) => prev,
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/admin/seo/sync');
      return res.data;
    },
    onSuccess: (d: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin-seo-list-all'] });
      toast.success(
        `Sincronização concluída: ${d.discovered ?? 0} rotas, ${d.new_pending ?? 0} novas.`,
      );
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Erro ao sincronizar rotas SEO');
    },
  });

  const routes = data?.routes ?? [];
  const total  = data?.total  ?? 0;

  // Agrupar por categoria
  const grouped: Record<string, SeoRoute[]> = {};
  for (const cat of CATEGORIES) {
    grouped[cat] = [];
  }
  grouped['Sem categoria'] = [];

  for (const route of routes) {
    const cat = route.category;
    if (cat && grouped[cat]) {
      grouped[cat].push(route);
    } else {
      grouped['Sem categoria'].push(route);
    }
  }

  const publishedTotal  = routes.filter((r) => r.published_at).length;
  const configuredTotal = routes.filter((r) => r.status === 'configured').length;

  return (
    <div className="space-y-6">
      {/* Configurações Globais */}
      <GlobalDefaults />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Globe className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">SEO Manager</h1>
            <p className="text-muted-foreground text-sm">
              Configurações SEO autodiscovery — páginas, jogos e modalidades
            </p>
          </div>
        </div>
        <Button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          variant="outline"
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          {syncMutation.isPending ? 'Sincronizando...' : 'Sincronizar Rotas'}
        </Button>
      </div>

      {/* Stats strip */}
      {!isLoading && (
        <div className="flex items-center gap-6 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <strong className="text-foreground">{publishedTotal}</strong> publicadas
          </span>
          <span className="flex items-center gap-1.5">
            <Globe className="h-4 w-4 text-sky-500" />
            <strong className="text-foreground">{configuredTotal}</strong> configuradas
          </span>
          <span className="text-muted-foreground">
            {total} total{isFetching ? ' · atualizando...' : ''}
          </span>
        </div>
      )}

      {/* Accordion por categoria */}
      <div className="rounded-lg border overflow-hidden divide-y">
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            Carregando rotas SEO...
          </div>
        ) : total === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            Nenhuma rota encontrada. Clique em "Sincronizar Rotas" para descobrir as páginas.
          </div>
        ) : (
          <>
            {[...CATEGORIES, 'Sem categoria'].map((cat, i) => {
              const catRoutes = grouped[cat] ?? [];
              if (catRoutes.length === 0) return null;
              return (
                <CategoryAccordion
                  key={cat}
                  categoryName={cat}
                  routes={catRoutes}
                  defaultOpen={i === 0}
                />
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
